import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ResultEvidencePreview from '../components/ResultEvidencePreview';
import SuggestInput from '../components/SuggestInput';
import { useAppState } from '../context/AppContext';
import { useCan } from '../hooks/useCan';
import {
  fetchCustomers,
  fetchInspection,
  fetchPaperGrades,
  fetchPaperWidths,
  inspectionImageUrl,
  inspectionSourceImageUrl,
  updateInspection,
  updateInspectionWithImage,
} from '../services/api';
import { renderAnnotatedEvidence } from '../utils/annotateEvidence';
import { evaluateAppearanceRules } from '../utils/evaluateRules';
import { loadImageFromUrl } from '../utils/imageFromFile';
import './ScanResultPage.css';

const emptyForm = {
  customerId: '',
  customerName: '',
  pdr: '',
  grade: '',
  gram: '',
  paperWidth: '',
  sizeW: '',
  sizeH: '',
  inspector: '',
};

/** ตัวอักษร (ไทย/อังกฤษ) + เครื่องหมายสระ/วรรณยุกต์ + เว้นวรรค */
function lettersOnly(value) {
  // \p{L}=ตัวอักษร, \p{M}=สระ/วรรณยุกต์ซ้อน (ิ ุ ์ ฯลฯ) — ห้ามตัดทิ้ง
  return String(value || '').replace(/[^\p{L}\p{M}\s]/gu, '');
}

/** ตัวเลขเท่านั้น */
function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '');
}

function parseSizeParts(meta = {}) {
  if (meta.sizeW || meta.sizeH) {
    return {
      sizeW: digitsOnly(meta.sizeW),
      sizeH: digitsOnly(meta.sizeH),
    };
  }
  const raw = String(meta.size || '');
  const paired = raw.match(/(\d+)\s*[x×*]\s*(\d+)/i);
  if (paired) {
    return { sizeW: paired[1], sizeH: paired[2] };
  }
  const first = raw.match(/(\d+)/);
  return { sizeW: first ? first[1] : '', sizeH: '' };
}

function formatSize(sizeW, sizeH) {
  if (sizeW && sizeH) return `${sizeW}x${sizeH}`;
  return sizeW || sizeH || '';
}

function formFromRow(data, fallbackMeta = {}) {
  const meta = { ...fallbackMeta, ...(data.meta || {}) };
  const sizeParts = parseSizeParts(meta);
  return {
    customerId: meta.customerId || '',
    customerName: meta.customerName || '',
    pdr: meta.pdr || '',
    grade: meta.grade || '',
    gram: meta.gram != null && meta.gram !== '' ? String(meta.gram) : '',
    paperWidth:
      meta.paperWidth != null && meta.paperWidth !== ''
        ? String(meta.paperWidth)
        : '',
    sizeW: sizeParts.sizeW,
    sizeH: sizeParts.sizeH,
    inspector: lettersOnly(meta.inspector || meta.operator || ''),
  };
}

function spotRowKey(id, index) {
  return `${id || 'spot'}::${index}`;
}

function buildSpotRows(row) {
  const overlays = Array.isArray(row?.overlaySpots) ? row.overlaySpots : [];
  const spots = Array.isArray(row?.spots) ? row.spots : [];
  const count = Math.max(overlays.length, spots.length, 0);

  return Array.from({ length: count }, (_, index) => {
    const overlay = overlays[index] || {};
    const spot = spots[index] || {};
    const id = spot.id || overlay.id || `spot-${index + 1}`;
    const rowKey = spotRowKey(id, index);
    const diameterMm = Number(spot.diameterMm ?? overlay.diameterMm) || 0;
    const type = spot.type || overlay.type || 'black_spot';

    return {
      rowKey,
      id,
      overlay: {
        ...overlay,
        id,
        rowKey,
        diameterMm,
        type,
        isKnot: Boolean(overlay.isKnot || type === 'wood_knot'),
        oversized:
          overlay.oversized ||
          diameterMm > (row?.rules?.maxBlackSpotDiameterMm || 5),
      },
      spot: {
        id,
        diameterMm,
        xMm: spot.xMm ?? null,
        yMm: spot.yMm ?? null,
        type,
      },
    };
  });
}

function buildSelectedKeys(rows, excludedSpotIds = []) {
  const excluded = new Set(excludedSpotIds || []);
  if (!excluded.size) return new Set(rows.map((row) => row.rowKey));
  return new Set(rows.filter((row) => !excluded.has(row.rowKey)).map((row) => row.rowKey));
}

function judgedFromSelection(rows, selectedKeys, rules, sampleAreaSqFt) {
  const activeSpots = rows.filter((row) => selectedKeys.has(row.rowKey)).map((row) => row.spot);
  const defects = activeSpots
    .filter((spot) => spot.type === 'wood_knot')
    .map((spot) => ({
      type: 'wood_knot',
      relatedSpotId: spot.id,
      diameterMm: spot.diameterMm,
    }));

  return evaluateAppearanceRules(activeSpots, defects, {
    maxSpotDiameterMm: rules?.maxBlackSpotDiameterMm ?? 5,
    largeSpotThresholdMm: rules?.largeSpotThresholdMm ?? 5,
    minDistanceBetweenLargeSpotsCm: rules?.minDistanceBetweenLargeSpotsCm ?? 8,
    maxBlackSpotsPerSqFt: rules?.maxBlackSpotsPerSqFt ?? 5,
    sampleAreaSqFt: sampleAreaSqFt || 1,
  });
}

function spotKindLabel(spot) {
  return spot?.type === 'wood_knot' || spot?.isKnot ? 'ตาไม้' : 'จุดด่างดำ';
}

export default function ScanResultPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { rules, meta, setMeta } = useAppState();
  const canUpdate = useCan('inspections.update');
  const canExportCoa = useCan('inspections.export_coa');
  const canCreate = useCan('inspections.create');

  const [row, setRow] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [customers, setCustomers] = useState([]);
  const [grades, setGrades] = useState([]);
  const [widths, setWidths] = useState([]);
  const [spotRows, setSpotRows] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editing, setEditing] = useState(true);
  const [error, setError] = useState('');

  const effectiveRules = row?.rules || rules;
  const sampleAreaSqFt = row?.sampleAreaSqFt || effectiveRules.sampleAreaSqFt || 1;

  const judged = useMemo(
    () => judgedFromSelection(spotRows, selectedKeys, effectiveRules, sampleAreaSqFt),
    [spotRows, selectedKeys, effectiveRules, sampleAreaSqFt]
  );

  const gradeSpecOptions = useMemo(() => {
    const list = [];
    const seen = new Set();
    const activeGrades = grades.filter((g) => g.active !== false);

    activeGrades.forEach((g) => {
      const grams = Array.isArray(g.grams) && g.grams.length ? g.grams.map(String) : [''];
      grams.forEach((gram) => {
        const value = `${g.code}|${gram}`;
        if (seen.has(value)) return;
        seen.add(value);
        list.push({
          value,
          label: gram ? `${g.code} ${gram}` : g.code,
        });
      });
    });

    const current = `${form.grade || ''}|${form.gram || ''}`;
    if (form.grade && !seen.has(current)) {
      list.unshift({
        value: current,
        label: form.gram ? `${form.grade} ${form.gram}` : form.grade,
      });
    }
    return list;
  }, [grades, form.grade, form.gram]);

  const widthOptions = useMemo(() => {
    const list = widths
      .filter((w) => w.active !== false)
      .map((w) => String(w.mm));
    if (form.paperWidth && !list.includes(String(form.paperWidth))) {
      return [String(form.paperWidth), ...list];
    }
    return list;
  }, [widths, form.paperWidth]);

  const gradeSpecText = form.gram
    ? `${form.grade || ''} ${form.gram}`.trim()
    : form.grade || '';

  const hasGradeSpec = Boolean(String(form.grade || '').trim());

  const verdict = judged.verdict;
  const spotCount = judged.spotCount;
  const hasSpotPicker = spotRows.length > 0;
  const previewImageUrl =
    inspectionSourceImageUrl(row) || inspectionImageUrl(row);
  const canInteractivePreview =
    hasSpotPicker &&
    Boolean(previewImageUrl) &&
    spotRows.some((item) => Number.isFinite(Number(item.overlay?.nx)));

  useEffect(() => {
    fetchCustomers()
      .then((data) => setCustomers(data || []))
      .catch(() => setCustomers([]));
    fetchPaperGrades(true)
      .then((data) => setGrades(data || []))
      .catch(() => setGrades([]));
    fetchPaperWidths(true)
      .then((data) => setWidths(data || []))
      .catch(() => setWidths([]));
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    fetchInspection(id)
      .then((data) => {
        if (!alive || !data) return;
        const rows = buildSpotRows(data);
        setRow(data);
        setSpotRows(rows);
        setSelectedKeys(buildSelectedKeys(rows, data.excludedSpotIds));
        setForm(formFromRow(data, meta));
        const alreadySaved = Boolean(data.updatedAt);
        setEditing(!alreadySaved);
        setSaved(alreadySaved);
      })
      .catch((err) => {
        if (alive) setError(err.message || 'โหลดผลสแกนไม่สำเร็จ');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id, meta.grade, meta.gram, meta.paperWidth, meta.inspector, meta.operator, meta.customerId, meta.customerName, meta.pdr, meta.sizeW, meta.sizeH, meta.size]);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }

  function onGradeSpecType(raw) {
    const text = String(raw || '').replace(/\s+/g, ' ').trimStart();
    const normalized = text.trim().toUpperCase();
    const matched = gradeSpecOptions.find(
      (o) => o.label.toUpperCase() === normalized
    );
    if (matched) {
      const [grade = '', gram = ''] = matched.value.split('|');
      setForm((prev) => ({ ...prev, grade, gram }));
      setSaved(false);
      return;
    }

    const parts = normalized.split(' ').filter(Boolean);
    const grade = parts[0] || '';
    const gram = parts.length > 1 && /^\d+$/.test(parts[1]) ? parts[1] : '';
    setForm((prev) => ({
      ...prev,
      grade: grade || text.trim(),
      gram,
      paperWidth: grade ? prev.paperWidth : '',
    }));
    setSaved(false);
  }

  function onGradeSpecPick(opt) {
    const [grade = '', gram = ''] = String(opt.value || '').split('|');
    setForm((prev) => ({ ...prev, grade, gram }));
    setSaved(false);
  }

  function onPaperWidthType(raw) {
    const value = digitsOnly(raw);
    setForm((prev) => ({ ...prev, paperWidth: value }));
    setSaved(false);
  }

  function onPaperWidthPick(opt) {
    const value = digitsOnly(opt.value ?? opt.label ?? opt);
    setForm((prev) => ({ ...prev, paperWidth: value }));
    setSaved(false);
  }

  function onCustomerChange(customerId) {
    const found = customers.find((c) => c.id === customerId);
    setForm((prev) => ({
      ...prev,
      customerId: customerId || '',
      customerName: found?.name || '',
    }));
    setSaved(false);
  }

  function toggleSpot(rowKey) {
    if (!editing) return;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
    setSaved(false);
  }

  function selectAllSpots() {
    if (!editing) return;
    setSelectedKeys(new Set(spotRows.map((item) => item.rowKey)));
    setSaved(false);
  }

  function clearAllSpots() {
    if (!editing) return;
    setSelectedKeys(new Set());
    setSaved(false);
  }

  function startEdit() {
    setEditing(true);
    setSaved(false);
    setError('');
  }

  function cancelEdit() {
    if (!row) return;
    const rows = buildSpotRows(row);
    setSpotRows(rows);
    setSelectedKeys(buildSelectedKeys(rows, row.excludedSpotIds));
    setForm(formFromRow(row));
    setEditing(false);
    setSaved(true);
    setError('');
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!row || !editing) return;
    setSaving(true);
    setError('');

    try {
      const activeOverlays = spotRows
        .filter((item) => selectedKeys.has(item.rowKey))
        .map((item) => item.overlay);
      const excludedSpotIds = spotRows
        .filter((item) => !selectedKeys.has(item.rowKey))
        .map((item) => item.rowKey);

      const size = formatSize(form.sizeW, form.sizeH);
      const revision = {
        meta: {
          customerId: form.customerId,
          customerName: form.customerName,
          pdr: form.pdr,
          grade: form.grade,
          gram: form.gram,
          paperWidth: form.paperWidth,
          sizeW: form.sizeW,
          sizeH: form.sizeH,
          size,
          inspector: form.inspector,
          operator: form.inspector,
        },
        spots: judged.spots,
        defects: judged.defects,
        overlaySpots: spotRows.map((item) => item.overlay),
        excludedSpotIds,
        sampleAreaSqFt,
        rules: effectiveRules,
      };

      let updated;
      const sourceUrl = inspectionSourceImageUrl(row);
      if (sourceUrl && activeOverlays.length >= 0) {
        const frame = await loadImageFromUrl(sourceUrl);
        const annotated = await renderAnnotatedEvidence(frame.canvas, {
          overlaySpots: activeOverlays,
          verdict: judged.verdict,
          spotCount: judged.spotCount,
          maxSpotDiameterMm: effectiveRules.maxBlackSpotDiameterMm,
        });
        updated = await updateInspectionWithImage(
          row.id,
          revision,
          annotated.blob,
          `rollscan-${row.id}-${Date.now()}.jpg`
        );
      } else {
        updated = await updateInspection(row.id, revision);
      }

      setRow(updated);
      setSpotRows(buildSpotRows(updated));
      setSelectedKeys(buildSelectedKeys(buildSpotRows(updated), updated.excludedSpotIds));
      setMeta((m) => ({
        ...m,
        grade: form.grade,
        gram: form.gram,
        paperWidth: form.paperWidth,
        operator: form.inspector,
        inspector: form.inspector,
        customerId: form.customerId,
        customerName: form.customerName,
        pdr: form.pdr,
        sizeW: form.sizeW,
        sizeH: form.sizeH,
        size: formatSize(form.sizeW, form.sizeH),
      }));
      setSaved(true);
      setEditing(false);
    } catch (err) {
      setError(err.message || 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  }

  const readOnly = !editing || !canUpdate;
  const selectedCount = selectedKeys.size;
  const totalDetected = spotRows.length;
  const visibleSpotRows = readOnly
    ? spotRows.filter((item) => selectedKeys.has(item.rowKey))
    : spotRows;

  if (loading) {
    return (
      <div className="scan-result-page">
        <div className="card muted">กำลังโหลดผลสแกน...</div>
      </div>
    );
  }

  if (error && !row) {
    return (
      <div className="scan-result-page stack">
        <div className="card" style={{ color: 'var(--ng)' }}>{error}</div>
        <Link className="btn btn-primary" to="/scan">กลับไปสแกน</Link>
      </div>
    );
  }

  return (
    <div className="scan-result-page">
      <header className="result-hero">
        <div className="result-hero-top">
          <div>
            <p className="result-kicker">ผลการสแกน</p>
            <h1>ตรวจผิวกระดาษ</h1>
          </div>
          <span className={`badge result-verdict-badge ${verdict === 'OK' ? 'ok' : 'ng'}`}>
            {verdict === 'OK' ? 'ผ่าน' : 'ไม่ผ่าน'}
          </span>
        </div>
        <p className="muted result-summary">
          {readOnly || spotCount === totalDetected
            ? `พบ ${spotCount} จุด`
            : `ตรวจพบ ${totalDetected} จุด · ใช้ ${spotCount} จุด`}
          {readOnly
            ? ' · กำลังดูข้อมูล — กดแก้ไขเพื่อเปลี่ยน'
            : hasSpotPicker
              ? ' · ติ๊กเลือกจุดที่จะนับ แล้วกดบันทึก'
              : ' · กรอกข้อมูลด้านล่างแล้วกดบันทึก'}
        </p>
      </header>

      <div className="result-main-row">
        <section className="result-photo-card">
          <div className="result-photo-stage">
            {canInteractivePreview ? (
              <ResultEvidencePreview
                imageUrl={previewImageUrl}
                overlaySpots={spotRows.map((item) => item.overlay)}
                selectedKeys={selectedKeys}
                onToggleSpot={toggleSpot}
                readOnly={readOnly}
                spotCount={spotCount}
                verdict={verdict}
              />
            ) : previewImageUrl ? (
              <img
                src={inspectionImageUrl(row) || previewImageUrl}
                alt={`หลักฐานพบ ${spotCount} จุด`}
                className="result-evidence"
              />
            ) : (
              <div className="result-photo-empty muted">ไม่มีรูปหลักฐาน</div>
            )}
          </div>
          <div className="result-photo-meta">
            <span>{readOnly ? `พบ ${spotCount} จุด` : `ใช้ ${spotCount} / ${totalDetected || spotCount} จุด`}</span>
            <span>
              {judged.spotsPerSqFt != null
                ? `≈ ${judged.spotsPerSqFt}/ตร.ฟุต`
                : 'ตาม LFB-QAD-SP-004'}
            </span>
            <span>{new Date(row?.createdAt || Date.now()).toLocaleString()}</span>
          </div>
        </section>

        <form
          className={`result-form card stack${readOnly ? ' is-readonly' : ''}`}
          onSubmit={handleSubmit}
        >
          <div className="result-form-head">
            <div className="result-form-head-row">
              <h2>ข้อมูลการตรวจ</h2>
              {readOnly && canUpdate ? (
                <button className="btn" type="button" onClick={startEdit}>
                  แก้ไข
                </button>
              ) : null}
            </div>
            <p className="muted">
              {readOnly
                ? 'โหมดดูข้อมูล — กดแก้ไขหากต้องการเปลี่ยน'
                : 'กรอกข้อมูลหลังสแกน — สถานะผ่าน/ไม่ผ่านคำนวณจากจุดที่เลือก'}
            </p>
          </div>

          <div className="field field-span-2">
            <label>สถานะ (จากจุดที่เลือก)</label>
            <div className="result-status-locked" aria-label="สถานะ">
              <span className={`status-locked-badge ${verdict === 'OK' ? 'ok' : 'ng'}`}>
                {verdict === 'OK' ? 'ผ่าน' : 'ไม่ผ่าน'}
              </span>
              <span className="muted status-locked-note">
                {hasSpotPicker ? 'อัปเดตตามจุดที่ติ๊ก' : 'จากผลตรวจอัตโนมัติ'}
              </span>
            </div>
          </div>

          <div className="field field-span-2">
            <label htmlFor="customerId">ชื่อบริษัท</label>
            <select
              id="customerId"
              value={form.customerId}
              disabled={readOnly}
              onChange={(e) => onCustomerChange(e.target.value)}
            >
              <option value="">— เลือกลูกค้า —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.code ? ` (${c.code})` : ''}
                </option>
              ))}
            </select>
            {!readOnly && customers.length === 0 ? (
              <p className="muted" style={{ margin: 0, fontSize: '0.82rem' }}>
                ยังไม่มีลูกค้า — ไปเพิ่มที่เมนู อื่นๆ → ลูกค้า
              </p>
            ) : null}
          </div>

          <div className="field">
            <label htmlFor="inspector">ชื่อผู้ตรวจ</label>
            <input
              id="inspector"
              value={form.inspector}
              disabled={readOnly}
              onChange={(e) => setField('inspector', lettersOnly(e.target.value))}
              placeholder="ชื่อผู้ตรวจ"
              inputMode="text"
              autoComplete="name"
            />
          </div>

          <div className="field">
            <label htmlFor="pdr">PDR งาน</label>
            <input
              id="pdr"
              value={form.pdr}
              disabled={readOnly}
              onChange={(e) => setField('pdr', e.target.value)}
              placeholder="เช่น PDR-2408"
              autoComplete="off"
            />
          </div>

          <div className="field field-span-2">
            <label htmlFor="gradeSpec">เกรดกระดาษ</label>
            <div className="grade-spec-stack">
              <SuggestInput
                id="gradeSpec"
                value={gradeSpecText}
                disabled={readOnly}
                options={gradeSpecOptions}
                placeholder="พิมพ์หรือเลือก เช่น KA 125"
                onChange={onGradeSpecType}
                onPick={onGradeSpecPick}
              />

              {hasGradeSpec ? (
                <SuggestInput
                  id="paperWidth"
                  value={form.paperWidth}
                  disabled={readOnly}
                  options={widthOptions.map((w) => ({ value: w, label: String(w) }))}
                  placeholder="หน้ากว้าง (mm)"
                  inputMode="numeric"
                  aria-label="หน้ากว้าง (mm)"
                  onChange={onPaperWidthType}
                  onPick={onPaperWidthPick}
                />
              ) : null}
            </div>
            {!readOnly && grades.length === 0 ? (
              <p className="muted" style={{ margin: 0, fontSize: '0.82rem' }}>
                ยังไม่มีเกรด — ไปเพิ่มที่เมนู อื่นๆ → เกรดกระดาษ
              </p>
            ) : null}
          </div>

          <div className="field field-span-2">
            <label htmlFor="sizeW">ขนาดงาน (mm)</label>
            <div className="size-mm-pair">
              <input
                id="sizeW"
                value={form.sizeW}
                disabled={readOnly}
                onChange={(e) => setField('sizeW', digitsOnly(e.target.value))}
                placeholder="กว้าง"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
              />
              <span className="size-mm-x" aria-hidden="true">×</span>
              <input
                id="sizeH"
                value={form.sizeH}
                disabled={readOnly}
                onChange={(e) => setField('sizeH', digitsOnly(e.target.value))}
                placeholder="ยาว"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                aria-label="ขนาดงานด้านยาว (mm)"
              />
            </div>
          </div>

          {error && <p className="save-error">{error}</p>}

          <div className="result-form-actions">
            {canUpdate && editing ? (
              <>
                <button className="btn btn-primary" type="submit" disabled={saving}>
                  {saving ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
                </button>
                {row?.updatedAt ? (
                  <button className="btn" type="button" onClick={cancelEdit} disabled={saving}>
                    ยกเลิก
                  </button>
                ) : null}
              </>
            ) : null}
            {canUpdate && !editing ? (
              <button className="btn" type="button" onClick={startEdit}>
                แก้ไข
              </button>
            ) : null}
            {canExportCoa ? (
              <Link
                className={`btn${editing ? '' : ' btn-primary'}`}
                to={`/scan/result/${row.id}/coa`}
              >
                ออกเอกสาร COA
              </Link>
            ) : null}
            {canCreate ? (
              <button
                className="btn"
                type="button"
                onClick={() => navigate('/scan')}
              >
                สแกนต่อ
              </button>
            ) : null}
            <Link className="btn" to="/data">
              ไปที่ข้อมูล
            </Link>
          </div>
        </form>
      </div>

      {hasSpotPicker && (!readOnly || visibleSpotRows.length > 0) ? (
        <section className="result-spot-picker card stack">
          <div className="result-spot-picker-head">
            <div>
              <h2>{readOnly ? 'จุดที่นับ' : 'เลือกจุดที่จะนับ'}</h2>
              <p className="muted">
                {readOnly
                  ? 'รายการจุดที่ใช้ในการตัดสินผล'
                  : 'ยกเลิกติ๊กจุดที่ไม่ใช่ตำหนิ — ผลผ่าน/ไม่ผ่านจะอัปเดตทันที'}
              </p>
            </div>
            {!readOnly ? (
              <div className="result-spot-picker-actions">
                <button className="btn btn-sm" type="button" onClick={selectAllSpots}>
                  เลือกทั้งหมด
                </button>
                <button className="btn btn-sm" type="button" onClick={clearAllSpots}>
                  ไม่เอาทั้งหมด
                </button>
              </div>
            ) : null}
          </div>

          <ul className="result-spot-list">
            {visibleSpotRows.map((item, index) => {
              const checked = selectedKeys.has(item.rowKey);
              return (
                <li key={item.rowKey}>
                  <label className={`result-spot-item${checked ? '' : ' excluded'}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={readOnly}
                      onChange={() => toggleSpot(item.rowKey)}
                    />
                    <span className="result-spot-item-main">
                      <strong>
                        {index + 1}. {spotKindLabel(item.spot)} Ø{item.spot.diameterMm.toFixed(1)} มม.
                      </strong>
                      <span className="muted">
                        {checked ? 'นับในผลตรวจ' : 'ไม่นับ'}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>

          {!readOnly ? (
            <p className="muted result-spot-picker-note">
              เลือกแล้ว {selectedCount} จาก {totalDetected} จุด
            </p>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
